import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Users, PiggyBank } from "lucide-react";
import Disclaimer from "../components/Disclaimer";
import { trackEvent } from "../lib/ccsApi";

export default function Landing() {
    useEffect(() => { trackEvent("page_view", { page: "landing" }); }, []);

    return (
        <div className="bg-white">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        <div className="animate-fade-up">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] text-xs uppercase tracking-[0.2em] text-slate-600" data-testid="hero-badge">
                                <ShieldCheck className="h-3.5 w-3.5 text-[#22C55E]" /> Made for Australian parents
                            </span>
                            <h1 className="font-heading font-bold tracking-tight text-[#0F172A] text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]" data-testid="hero-headline">
                                Estimate Your Child Care<br/>
                                Costs in <span className="text-[#1E3A8A]">Seconds</span>.
                            </h1>
                            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl" data-testid="hero-subtext">
                                Understand your CCS and out-of-pocket expenses instantly. Built for Australian parents.
                            </p>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/calculator"
                                    onClick={() => trackEvent("cta_click", { cta: "start_calculator" })}
                                    className="ccs-btn-primary inline-flex items-center justify-center gap-2"
                                    data-testid="start-calculator-button"
                                >
                                    Start Calculator <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href="#how-it-works"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#E2E8F0] text-[#0F172A] hover:border-[#1E3A8A] transition-colors"
                                    data-testid="how-it-works-link"
                                >
                                    How it works
                                </a>
                            </div>

                            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                                <Stat value="2 min" label="to estimate" testid="stat-time" />
                                <Stat value="$14.63" label="LDC hourly cap" testid="stat-cap" />
                                <Stat value="95%" label="max subsidy" testid="stat-max" />
                            </div>
                        </div>

                        <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
                            <div className="relative rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-sm">
                                <img
                                    src="https://images.unsplash.com/photo-1758598737529-3b326b4c8a55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxwYXJlbnRzJTIwY2hpbGQlMjByZWFkaW5nJTIwdG9nZXRoZXJ8ZW58MHx8fHwxNzc3NTIwMjAxfDA&ixlib=rb-4.1.0&q=85"
                                    alt="Parent and child reading together"
                                    className="w-full h-[420px] lg:h-[520px] object-cover"
                                />
                                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl border border-[#E2E8F0] p-4 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-[#22C55E]/10 text-[#16A34A] flex items-center justify-center">
                                        <PiggyBank className="h-5 w-5" />
                                    </div>
                                    <div className="text-sm leading-tight">
                                        <div className="font-semibold text-[#0F172A]">Estimated weekly saving</div>
                                        <div className="text-slate-600">See your personalised breakdown in seconds</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how-it-works" className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                    <div className="max-w-2xl">
                        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Two ways to use it</span>
                        <h2 className="font-heading font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-3 text-[#0F172A]">
                            Pick your path — we do the maths.
                        </h2>
                    </div>

                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        <ModeCard
                            to="/calculator?mode=estimator"
                            icon={<Zap className="h-5 w-5" />}
                            eyebrow="Estimate"
                            title="Estimate my CCS percentage"
                            body="Share your family income and activity hours. We'll tell you the CCS % you're likely to receive under the current rules."
                            cta="Estimate CCS %"
                            testid="mode-card-estimator"
                        />
                        <ModeCard
                            to="/calculator?mode=out_of_pocket"
                            icon={<PiggyBank className="h-5 w-5" />}
                            eyebrow="Out-of-pocket"
                            title="Calculate my out-of-pocket cost"
                            body="Already know your CCS %? Enter your centre's hourly fee and care pattern to see your real gap fee per day, fortnight and month."
                            cta="Calculate costs"
                            testid="mode-card-oop"
                        />
                    </div>

                    <div className="mt-10">
                        <Disclaimer />
                    </div>
                </div>
            </section>

            {/* Why use */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                <div className="grid md:grid-cols-3 gap-6">
                    <Feature
                        icon={<ShieldCheck className="h-5 w-5" />}
                        title="Always up to date"
                        body="Income thresholds, hourly caps and activity test rules — all aligned with the latest Services Australia guidance."
                    />
                    <Feature
                        icon={<Users className="h-5 w-5" />}
                        title="Multi-child aware"
                        body="Applies the higher CCS rate automatically for your second and younger children aged 5 or under."
                    />
                    <Feature
                        icon={<Zap className="h-5 w-5" />}
                        title="Clear breakdowns"
                        body="Every field has a tooltip. Every result has a clear step-by-step breakdown."
                    />
                </div>
            </section>
        </div>
    );
}

function Stat({ value, label, testid }) {
    return (
        <div data-testid={testid}>
            <div className="font-heading font-bold text-2xl text-[#0F172A]">{value}</div>
            <div className="text-xs text-slate-500 mt-1">{label}</div>
        </div>
    );
}

function ModeCard({ to, icon, eyebrow, title, body, cta, testid }) {
    return (
        <Link
            to={to || "/calculator"}
            className="group ccs-card hover:-translate-y-1 hover:shadow-sm transition-all block"
            data-testid={testid}
            onClick={() => trackEvent("cta_click", { cta: "mode_card", which: testid })}
        >
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center">
                    {icon}
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{eyebrow}</span>
            </div>
            <h3 className="font-heading font-semibold text-xl mt-5 text-[#0F172A]">{title}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-[#1E3A8A] font-medium group-hover:gap-3 transition-all">
                {cta} <ArrowRight className="h-4 w-4" />
            </div>
        </Link>
    );
}

function Feature({ icon, title, body }) {
    return (
        <div className="ccs-card">
            <div className="h-9 w-9 rounded-full bg-[#22C55E]/10 text-[#16A34A] flex items-center justify-center">{icon}</div>
            <h4 className="font-heading font-semibold text-lg mt-4 text-[#0F172A]">{title}</h4>
            <p className="mt-2 text-slate-600">{body}</p>
        </div>
    );
}
